// Static palette used by the activity chart's stacked layers and the
// trace-detail tone swatches. Each id is referenced from the layer
// metadata returned by overview analytics (which carries layer.color); these
// names are the canonical mapping.
export const layerPalette = {
  inference: "#7bc48f",
  code: "#7a76ff",
  files: "#e8b159",
  network: "#64bdd8",
  command: "#f17154",
  memory: "#d887ff",
  surface: "#f5efe6",
  ink: "#12100d",
};

// ── Shared theme-version hook ─────────────────────────────────────
//
// Many canvas renderers (DotGrid, SurfaceChart, RenderMount, …) need
// to re-resolve CSS-var colors when the user toggles light/dark mode.
// Each previously stood up its own MutationObserver on the
// <html>/<body> `data-theme` attribute. With a registry-sized cluster
// of charts on screen, that's N observers + N callbacks fanning out
// on every theme flip.
//
// useThemeVersion() shares a single module-level observer across all
// callers via subscriber refcounting: the observer attaches the first
// time anyone subscribes and tears down when the last unmounts. The
// returned integer bumps on every `data-theme` mutation, suitable as
// a useMemo / useEffect dep so consumers can rebuild their cached
// theme reads (`getComputedStyle(...)`, `resolveCanvasColor(...)`,
// `readTheme()`, etc.).

import { useSyncExternalStore } from "react";

let themeVersion = 0;
const themeSubscribers = new Set();
let themeObserver = null;
let themeObserverRefcount = 0;

function notifyThemeSubscribers() {
  themeVersion += 1;
  for (const cb of themeSubscribers) cb();
}

function attachThemeObserver() {
  if (themeObserver || typeof document === "undefined") return;
  themeObserver = new MutationObserver((mutations) => {
    if (mutations.some((m) => m.attributeName === "data-theme")) {
      notifyThemeSubscribers();
    }
  });
  const opts = { attributes: true, attributeFilter: ["data-theme"] };
  if (document.documentElement) themeObserver.observe(document.documentElement, opts);
  if (document.body) themeObserver.observe(document.body, opts);
}

function detachThemeObserver() {
  if (!themeObserver) return;
  themeObserver.disconnect();
  themeObserver = null;
}

function subscribeThemeVersion(cb) {
  themeSubscribers.add(cb);
  if (themeObserverRefcount === 0) attachThemeObserver();
  themeObserverRefcount += 1;
  return () => {
    themeSubscribers.delete(cb);
    themeObserverRefcount -= 1;
    if (themeObserverRefcount === 0) detachThemeObserver();
  };
}

function getThemeVersionSnapshot() {
  return themeVersion;
}

export function useThemeVersion() {
  return useSyncExternalStore(
    subscribeThemeVersion,
    getThemeVersionSnapshot,
    getThemeVersionSnapshot,
  );
}
